import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Theme, PageTheme, ThemeTokens } from '@/types/theme';

const THEMES_COLLECTION = 'themes';
const PAGE_THEMES_COLLECTION = 'pageThemes';

// Get default platform theme
export async function getDefaultPlatformTheme(): Promise<Theme | null> {
  try {
    const q = query(
      collection(db, THEMES_COLLECTION),
      where('scope', '==', 'platform'),
      where('isDefault', '==', true),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const themeDoc = snapshot.docs[0];
    return { id: themeDoc.id, ...themeDoc.data() } as Theme;
  } catch (error) {
    console.error('Error getting default platform theme:', error);
    return null;
  }
}

// Get a specific theme by ID
export async function getTheme(themeId: string): Promise<Theme | null> {
  try {
    const themeRef = doc(db, THEMES_COLLECTION, themeId);
    const themeDoc = await getDoc(themeRef);

    if (!themeDoc.exists()) {
      return null;
    }

    return { id: themeDoc.id, ...themeDoc.data() } as Theme;
  } catch (error) {
    console.error('Error getting theme:', error);
    return null;
  }
}

// Get all platform themes
export async function getPlatformThemes(): Promise<Theme[]> {
  try {
    const q = query(
      collection(db, THEMES_COLLECTION),
      where('scope', '==', 'platform'),
      where('status', '==', 'active'),
      orderBy('name', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Theme));
  } catch (error) {
    console.error('Error getting platform themes:', error);
    return [];
  }
}

// Get user's custom themes
export async function getUserThemes(userId: string): Promise<Theme[]> {
  try {
    const q = query(
      collection(db, THEMES_COLLECTION),
      where('scope', '==', 'user'),
      where('createdByUid', '==', userId),
      orderBy('updatedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Theme));
  } catch (error) {
    console.error('Error getting user themes:', error);
    return [];
  }
}

// Get page-specific theme
export async function getPageTheme(pageKey: string): Promise<PageTheme | null> {
  try {
    const q = query(
      collection(db, PAGE_THEMES_COLLECTION),
      where('pageKey', '==', pageKey),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const pageThemeDoc = snapshot.docs[0];
    return { id: pageThemeDoc.id, ...pageThemeDoc.data() } as PageTheme;
  } catch (error) {
    console.error('Error getting page theme:', error);
    return null;
  }
}

// Create a new theme
export async function createTheme(themeData: Omit<Theme, 'id' | 'updatedAt'>): Promise<string> {
  try {
    const themeRef = doc(collection(db, THEMES_COLLECTION));

    const theme: Omit<Theme, 'id'> = {
      ...themeData,
      updatedAt: Timestamp.now(),
    };

    await setDoc(themeRef, theme);
    return themeRef.id;
  } catch (error) {
    console.error('Error creating theme:', error);
    throw error;
  }
}

// Update an existing theme
export async function updateTheme(
  themeId: string,
  updates: Partial<Omit<Theme, 'id'>>
): Promise<void> {
  try {
    const themeRef = doc(db, THEMES_COLLECTION, themeId);

    await updateDoc(themeRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating theme:', error);
    throw error;
  }
}

// Delete a theme
export async function deleteTheme(themeId: string): Promise<void> {
  try {
    const themeRef = doc(db, THEMES_COLLECTION, themeId);
    await deleteDoc(themeRef);
  } catch (error) {
    console.error('Error deleting theme:', error);
    throw error;
  }
}

// Set a theme as the default platform theme
export async function setDefaultPlatformTheme(themeId: string): Promise<void> {
  try {
    const batch = writeBatch(db);

    // First, unset all other platform themes' isDefault flag
    const q = query(
      collection(db, THEMES_COLLECTION),
      where('scope', '==', 'platform'),
      where('isDefault', '==', true)
    );

    const snapshot = await getDocs(q);
    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, { isDefault: false });
    });

    // Then set the new default theme
    const newDefaultRef = doc(db, THEMES_COLLECTION, themeId);
    batch.update(newDefaultRef, { isDefault: true, updatedAt: Timestamp.now() });

    await batch.commit();
  } catch (error) {
    console.error('Error setting default platform theme:', error);
    throw error;
  }
}

// Subscribe to theme changes (real-time)
export function subscribeToTheme(
  themeId: string,
  callback: (theme: Theme | null) => void
): () => void {
  const themeRef = doc(db, THEMES_COLLECTION, themeId);

  return onSnapshot(
    themeRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback({ id: snapshot.id, ...snapshot.data() } as Theme);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error('Error subscribing to theme:', error);
      callback(null);
    }
  );
}

// Create or update a page theme
export async function setPageTheme(
  pageKey: string,
  themeId: string,
  overrides: Partial<ThemeTokens> = {}
): Promise<void> {
  try {
    // Check if page theme already exists
    const q = query(
      collection(db, PAGE_THEMES_COLLECTION),
      where('pageKey', '==', pageKey)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Create new page theme
      const pageThemeRef = doc(collection(db, PAGE_THEMES_COLLECTION));
      const pageTheme: Omit<PageTheme, 'id'> = {
        pageKey,
        themeId,
        overrides,
        status: 'active',
        updatedAt: Timestamp.now(),
      };
      await setDoc(pageThemeRef, pageTheme);
    } else {
      // Update existing page theme
      const pageThemeDoc = snapshot.docs[0];
      await updateDoc(pageThemeDoc.ref, {
        themeId,
        overrides,
        updatedAt: Timestamp.now(),
      });
    }
  } catch (error) {
    console.error('Error setting page theme:', error);
    throw error;
  }
}

// Delete a page theme
export async function deletePageTheme(pageKey: string): Promise<void> {
  try {
    const q = query(
      collection(db, PAGE_THEMES_COLLECTION),
      where('pageKey', '==', pageKey)
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const pageThemeDoc = snapshot.docs[0];
      await deleteDoc(pageThemeDoc.ref);
    }
  } catch (error) {
    console.error('Error deleting page theme:', error);
    throw error;
  }
}

// Update user's profile theme preference
export async function setUserProfileTheme(userId: string, themeId: string | null): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      profileThemeId: themeId,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error setting user profile theme:', error);
    throw error;
  }
}
